import {
  EPrivateRouteType,
  IMenuList
} from "components/Layout/ManagerProLayout/utils";
import type { SidebarIconRailItem } from "../sidebar-icon-rail";
import { canShowMenuItem, type SidebarMenuUser } from "./sidebar-menu-access";
import { resolveSidebarChildTone, resolveSidebarTone } from "./resolve-tone";
import { SidebarSvgIcon } from "./sidebar-icon";
import type { SidebarNavItem } from "./types";
import { ICONS_MODULES_PATHS } from "./sidebar-icon-modules-path";

const FALLBACK_ICON = ICONS_MODULES_PATHS.EXPLORE.EXPLORE_MODULE;

function renderMenuIcon(
  item: Pick<IMenuList, "moduleIcon" | "urlIcon">,
  size: number
) {
  return (
    <SidebarSvgIcon
      path={item.moduleIcon || item.urlIcon || FALLBACK_ICON}
      size={size}
    />
  );
}

function slugId(name: string, path?: string): string {
  if (path) {
    return path.replace(/^\//, "").replace(/\//g, "-") || "root";
  }
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function mapMenuChild(item: IMenuList, user: SidebarMenuUser): SidebarNavItem | null {
  if (!canShowMenuItem(item, user) || !item.route?.path) return null;

  return {
    id: slugId(item.name, item.route.path),
    name: item.name,
    icon: renderMenuIcon(item, 14),
    path: item.route.path
  };
}

function mapMenuTopLevel(item: IMenuList, user: SidebarMenuUser): SidebarNavItem | null {
  if (!canShowMenuItem(item, user)) return null;

  if (item.type === EPrivateRouteType.ITEM && item.route?.path) {
    const id = slugId(item.name, item.route.path);
    const tone = resolveSidebarTone(id, item.route.path);

    return {
      id,
      name: item.name,
      icon: renderMenuIcon(item, 16),
      path: item.route.path,
      tone
    };
  }

  if (item.type === EPrivateRouteType.DROPDOWN && item.children?.length) {
    const children = item.children
      .map((child) => mapMenuChild(child, user))
      .filter((child): child is SidebarNavItem => child !== null);

    if (children.length === 0) return null;

    const id = slugId(item.name);
    const childPaths = children
      .map((child) => child.path)
      .filter((path): path is string => Boolean(path));
    const tone = resolveSidebarTone(id, childPaths[0]);

    return {
      id,
      name: item.name,
      icon: renderMenuIcon(item, 16),
      tone,
      children: children.map((child) => ({
        ...child,
        tone: resolveSidebarChildTone(tone)
      }))
    };
  }

  return null;
}

/** Ítems de primer nivel — mismos que `MainMenuList` / sidebar original. */
export function buildTopLevelSidebarNav(
  menuItems: IMenuList[],
  user: SidebarMenuUser
): SidebarNavItem[] {
  return menuItems
    .map((item) => mapMenuTopLevel(item, user))
    .filter((item): item is SidebarNavItem => item !== null);
}

/**
 * Rail de iconos del sidebar v2.
 *
 * - ITEM types → `path` directo, sin `children`.
 * - DROPDOWN types → `children` con los subitems; sin `path` (flyout abre el grupo).
 */
export function buildPrimaryRailItems(
  topLevel: SidebarNavItem[]
): SidebarIconRailItem[] {
  const railItems: SidebarIconRailItem[] = [];

  topLevel.forEach((item, index) => {
    if (item.children?.length) {
      const childPaths = item.children
        .map((c) => c.path)
        .filter((p): p is string => Boolean(p));

      if (childPaths.length === 0) return;

      const tone = resolveSidebarTone(item.id, childPaths[0], index);

      railItems.push({
        id: item.id,
        name: item.name,
        icon: item.icon,
        tone,
        matchPaths: childPaths,
        children: item.children
          .filter((c): c is SidebarNavItem & { path: string } => Boolean(c.path))
          .map((c) => ({
            id: c.id,
            name: c.name,
            path: c.path,
            icon: c.icon,
            tone: resolveSidebarChildTone(tone)
          }))
      });
    } else if (item.path) {
      const tone = resolveSidebarTone(item.id, item.path, index);

      railItems.push({
        id: item.id,
        name: item.name,
        path: item.path,
        icon: item.icon,
        tone,
        matchPaths: [item.path]
      });
    }
  });

  return railItems;
}
